/**
 * @file SiteViewLayout.tsx
 * @description Каркас «Сайт» (phase19.16 Р1, Р1-6а): общий черновик вида
 * сайта, «что изменено», права по частям и общие окна (публикация, «Что
 * изменено», отмена изменений, предпросмотр на весь экран) для
 * конструктора сайта.
 *
 * Смотреть вид сайта может любой, у кого есть право правки хотя бы одной
 * части (контракт §0, `canViewSiteDraft`); остальным — объяснение вместо
 * перехода на «Нет доступа». Права разделов проверяет конструктор.
 *
 * Здесь же — свои шрифты магазина (Р1-7, SB99, `ShopFontsContext`): список
 * грузится при праве настроек магазина; удаление шрифта перечитывает
 * оформление черновика (сервер мог перевести роли на шрифт по умолчанию).
 */

import { canViewSiteDraft, SITE_VIEW_PART_RIGHTS } from "@chaiberi/shared";
import { Lock } from "lucide-react";
import { useCallback } from "react";
import { Link, Outlet } from "react-router";

import { useCurrentContext } from "../../../auth/hooks";
import { ChangesSheet } from "../../../components/site-view/dialogs/ChangesSheet";
import { DiscardDialog } from "../../../components/site-view/dialogs/DiscardDialog";
import { PublishSiteDialog } from "../../../components/site-view/dialogs/PublishSiteDialog";
import { SitePreviewDialog } from "../../../components/site-view/preview/SitePreviewDialog";
import { ShopFontsContext } from "../../../components/site-view/shop-fonts-context";
import { SiteViewContext } from "../../../components/site-view/site-view-context";
import { Button } from "../../../components/ui/button";
import { EmptyState } from "../../../components/ui/EmptyState";
import { useShopFontsState } from "../../../hooks/site-view/useShopFontsState";
import { useSiteViewState } from "../../../hooks/site-view/useSiteViewState";
import { siteView } from "../../../lib/api-site-view";

export function SiteViewLayout() {
  const ctx = useCurrentContext();
  const allowed = canViewSiteDraft(ctx?.role.permissions ?? []);
  const state = useSiteViewState(allowed);
  // Свои шрифты — часть оформления: то же право, что у правки THEME.
  const canManageFonts = (ctx?.role.permissions ?? []).includes(SITE_VIEW_PART_RIGHTS.THEME.edit);
  const { applyPart } = state;
  const refreshTheme = useCallback(async () => {
    try {
      const res = await siteView.theme();
      applyPart(res.part, { theme: res.theme });
    } catch {
      // Не перечиталось — оформление обновится при следующем входе.
    }
  }, [applyPart]);
  const fonts = useShopFontsState({ enabled: allowed && canManageFonts, canManage: canManageFonts, onThemeChanged: refreshTheme });

  if (!allowed) {
    return (
      <EmptyState
        variant="forbidden"
        icon={Lock}
        title="Нет доступа к виду сайта"
        description="Вид сайта меняют владелец магазина и сотрудники с правом настроек или контента."
        action={
          <Button asChild variant="outline">
            <Link to={`/shop/${ctx?.shop?.slug ?? ""}`}>На сводку</Link>
          </Button>
        }
      />
    );
  }

  return (
    <SiteViewContext.Provider value={state}>
      <ShopFontsContext.Provider value={fonts}>
        <Outlet />
        <PublishSiteDialog />
        <ChangesSheet />
        <DiscardDialog />
        <SitePreviewDialog open={state.dialog === "preview"} onClose={() => state.openDialog(null)} />
      </ShopFontsContext.Provider>
    </SiteViewContext.Provider>
  );
}
